<template>
  <div>
    <PageTitle title="Common Questions" />

    <v-divider></v-divider>

    <v-expansion-panels variant="accordion">
      <v-expansion-panel
        v-for="(question, idx) in quests"
        :key="idx"
        @click.stop="currentQuestion = question"
      >
        <!-- Panel Title -->
        <v-expansion-panel-title class="d-flex justify-space-between align-center pa-0 py-0 ps-0">
          <div class="d-flex align-center">
            <v-list-item class="pe-0 me-4 ma-3 ms-4 pa-0 custom-list-item">
              
              <div class="icon-wrapper">
                <icon fill="rgb(var(--v-theme-primary))" :width="26" :height="26">
                  {{ question.icon }}
                </icon>
              </div>
            </v-list-item>

            <span class="text-body-2 text-primary">
              {{ question.question }}
            </span>
          </div>
        </v-expansion-panel-title>

        <!-- Panel Content -->
        <v-expansion-panel-text>
          <!-- Platform specific steps (sections: iOS version + browser aware) -->
          <template v-if="filteredSections(currentQuestion).length">
            <div
              v-for="(section, sIdx) in filteredSections(currentQuestion)"
              :key="`${currentQuestion.question}-section-${sIdx}`"
            >
              <!-- <h5
                v-if="section.sectionTitle"
                class="font-weight-bold mt-5 mb-2 ps-4 pe-5 text-primary"
              >
                {{ section.sectionTitle }}
              </h5> -->
              <div
                v-for="(step, stepIdx) in section.steps"
                :key="`${currentQuestion.question}-${sIdx}-${stepIdx}`"
                class="rounded-xl mt-5 ps-4 pe-5"
              >
                <h6 class="mb-0">
                  {{ step.step }}
                </h6>
                <h6
                  v-if="step.header"
                  class="font-weight-bold my-0 py-2"
                >
                  {{ step.header }}
                </h6>
                <div v-if="step.message" class="text-body-2 my-0">
                  {{ step.message }}
                </div>
                <v-img
                  :alt="step.message || step.header"
                  class="mt-0 rounded"
                  cover
                  :src="step.media"
                />
                <v-divider />
              </div>
              <v-divider
                v-if="sIdx < filteredSections(currentQuestion).length - 1"
                class="my-4"
              />
            </div>
          </template>

          <!-- FAQ answers -->
          <div
            v-for="(q, idx) in (currentQuestion.questions || [])"
            :key="idx"
          >
            <h6 class="mb-3">
              {{ q.offer ? q.questions : q.question }}
            </h6>

            <p class="text-body-2 faq-answer" v-html="q.answer" />
          </div>
        </v-expansion-panel-text>
      </v-expansion-panel>
    </v-expansion-panels>

    <v-divider />

    <!-- Help section -->
    <span
      v-if="partnerUser?.id"
      class="d-flex flex-column align-center py-4"
    >
      Still Have Questions?

      <button
        class="text-primary mt-1 d-flex align-center"
        @click="openIntercom"
      >
        Chat with us now
      </button>

      <confirmation-modal
        v-if="modals.confirmation"
        :value="modals.confirmation"
        @close-modal="modals.confirmation = false"
      />

      <need-help-modal
        v-if="modals.needHelp"
        :value="modals.needHelp"
        @close-modal="modals.needHelp = false"
        @confirmation-modal="modals.confirmation = $event"
      />
    </span>
  </div>
</template>

<script>
import MD from 'mobile-detect'
import { mapGetters, mapState } from 'vuex'

import PageTitle from '@/components/shared/PageTitle/index.vue'
import NeedHelp from '../Shared/Modals/NeedHelp.vue'
import Confirmation from '../Shared/Modals/Confirmation.vue'
import { questions } from './data.js'
import { initializeIntercom, openIntercom } from '@/utils/intercom'

export default {
  name: 'Questions',

  components: {
    PageTitle,
    'need-help-modal': NeedHelp,
    'confirmation-modal': Confirmation
  },

  data () {
    return {
      currentQuestion: {},
      iosPlatformVersionHint: null,
      modals: {
        confirmation: false,
        needHelp: false
      }
    }
  },

  mounted () {
    this.initializeIntercom()
    this.resolveIosPlatformVersionFromClientHints()
  },

  computed: {
    ...mapGetters('deals', {
      getDeals: 'list'
    }),
    ...mapGetters('products', {
      standaloneProducts: 'standalone'
    }),
    ...mapState([
      'messages',
      'offer',
      'partner',
      'partnerUser'
    ]),

    deals () {
      return this.getDeals || []
    },

    mobileDetect () {
      return new MD(navigator.userAgent)
    },

    isAndroid () {
      return (
        this.mobileDetect.os() &&
        ['androidos'].includes(this.mobileDetect.os().toLowerCase())
      )
    },

    isIos () {
      return (
        this.mobileDetect.os() &&
        ['ios', 'ipados'].includes(this.mobileDetect.os().toLowerCase())
      )
    },

    mobileOs () {
      if (this.isIos) return 'ios'
      if (this.isAndroid) return 'android'
      return 'desktop'
    },

    iosVersionFromUserAgent () {
      if (!this.isIos) return null
      const ua = navigator.userAgent
      const patterns = [
        /iPhone OS (\d+)[\s_.]/i,
        /CPU iPhone OS (\d+)[\s_.]/i,
        /CPU OS (\d+)[\s_.]/i,
        /iPad;.*?OS (\d+)[\s_.]/i,
        /OS (\d+)[\s_.]/
      ]
      for (const re of patterns) {
        const m = ua.match(re)
        if (m) {
          const n = parseInt(m[1], 10)
          if (!Number.isNaN(n)) return n
        }
      }
      return null
    },

    /**
     * Best major iOS for FAQ gating: prefer Client Hints when higher than UA (fixes iOS 26 + frozen UA).
     */
    iosVersionForFiltering () {
      if (!this.isIos) return null
      const uaVer = this.iosVersionFromUserAgent
      const hint = this.iosPlatformVersionHint
      const safariHint = this.iosSafariVersionHint
      const candidates = [uaVer, hint, safariHint].filter(v => v != null)
      if (!candidates.length) return null
      return Math.max(...candidates)
    },

    iosBrowser () {
      if (!this.isIos) return null
      const ua = navigator.userAgent
      if (/CriOS/.test(ua)) return 'chrome'
      if (/FxiOS/.test(ua)) return 'firefox'
      if (/OPiOS/.test(ua)) return 'opera'
      if (/EdgiOS/.test(ua)) return 'edge'
      return 'safari'
    },

    /**
     * Safari UA may expose Version/26+ even when OS token is frozen/capped lower.
     */
    iosSafariVersionHint () {
      if (!this.isIos || this.iosBrowser !== 'safari') return null
      const match = navigator.userAgent.match(/Version\/(\d+)/i)
      if (!match) return null
      const major = parseInt(match[1], 10)
      return Number.isNaN(major) ? null : major
    },

    androidBrowser () {
      if (!this.isAndroid) return null
      const ua = navigator.userAgent
      if (/Firefox/.test(ua)) return 'firefox'
      if (/OPR/.test(ua) || /Opera/.test(ua)) return 'opera'
      if (/Edg/.test(ua)) return 'edge'
      if (/SamsungBrowser/.test(ua)) return 'samsung'
      return 'chrome'
    },

    currentBrowser () {
      if (this.isIos) return this.iosBrowser
      if (this.isAndroid) return this.androidBrowser
      return null
    },

    quests () {
      const qs = questions(this.offer, this.mobileOs)
      const faqs = Array.isArray(this.offer?.faqs) ? this.offer.faqs : []

      return faqs
        .map(faq => {
          if (faq.code) {
            const dq = qs.find(q => q?.code === faq.code)
            if (!dq) {
              return null
            }

            return {
              ...faq,
              ...dq,
              offer: true
            }
          }

          return {
            ...faq,
            icon: 'question',
            offer: true,
            questions: [
              {
                questions: faq.question,
                answer: faq.answer
              }
            ]
          }
        })
        .filter(faq => faq && faq.question !== '' && faq.showOnMobilePass)
    }
  },

  methods: {
    filteredSections (question) {
      const sections = question[this.mobileOs]
      if (!sections) return []
      const raw = this.iosVersionForFiltering
      const version =
        this.isIos && raw === null ? Infinity : raw
      return sections.filter(section => {
        if (this.isIos && version !== null) {
          if (section.minIosVersion && version < section.minIosVersion) {
            return false
          }
          if (section.maxIosVersion && version > section.maxIosVersion) {
            return false
          }
        }
        if (
          section.browsers &&
          this.currentBrowser &&
          !section.browsers.includes(this.currentBrowser)
        ) {
          return false
        }
        return true
      })
    },

    initializeIntercom () {
      initializeIntercom({
        partnerUser: this.partnerUser,
        partner: this.partner,
        deals: this.deals,
        standaloneProducts: this.standaloneProducts
      })
    },

    async resolveIosPlatformVersionFromClientHints () {
      if (!this.isIos || !navigator.userAgentData?.getHighEntropyValues) {
        return
      }
      try {
        const hints = await navigator.userAgentData.getHighEntropyValues([
          'platformVersion'
        ])
        const raw = hints.platformVersion
        if (raw == null || raw === '') return
        const major = parseInt(String(raw).split('.')[0], 10)
        if (!Number.isNaN(major) && major >= 1 && major <= 99) {
          this.iosPlatformVersionHint = major
        }
      } catch (_) {
        
      }
    },

    openIntercom () {
      openIntercom()
    }
  }
}
</script>

<style scoped lang="scss">
// .v-expansion-panel {
//   box-shadow: none;
// }

// :deep(.v-expansion-panel__shadow) {
//   box-shadow: 
//     0px 1px 1px 0px var(--v-shadow-key-umbra-opacity, rgba(0, 0, 0, 0.04)),
//     0px 1px 1px 0px var(--v-shadow-key-penumbra-opacity, rgba(0, 0, 0, 0.04)),
//     0px 1px 1px 0px var(--v-shadow-key-ambient-opacity, rgba(0, 0, 0, 0.04));
// }

:deep(.v-expansion-panel__shadow) {
  box-shadow: none;
}

.custom-list-item {
  min-width: 24px;
  max-width: 30px;
  height: 26px;
}
.icon-wrapper {
  width: 30px;
}
.v-application .text-body-2 {
  padding: 16px 0px 16px 20px;
}

:deep(.v-list-item.v-theme--light.v-list-item--density-default svg) {
  height: 30px;
  width: 26px;
}

:deep(.v-expansion-panel-title) {
  padding-right: 24px !important;
  padding-left: 0;
  padding-top: 0;
  padding-bottom: 0;
}

:deep(.v-expansion-panel-title__icon) {
  color: rgba(0, 0, 0, 0.54);
}

:deep(.v-expansion-panel-title__icon .v-icon) {
  font-size: calc(var(--v-icon-size-multiplier) * 1.5em - 0px);
}

:deep(.v-list-item.v-theme--light.v-list-item--density-default) {
  margin: 12px 12px 12px 16px;
  padding: 0;
  min-width: 24px;
  max-width: 30px;
  height: 26px;
}

:deep(.v-list-item__content) {
  min-width: 30px;
  max-width: 30px;
}

:deep(.faq-answer) {
  padding: 0px !important;
}

:deep(.v-expansion-panel-text__wrapper) {
  padding: 0px 24px 16px;
}

:deep(.v-divider.v-theme--light) {
  border-width: thin 0 0 0;
  opacity: 1;
}
</style>